import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-coral hover:text-coral/80 mb-8">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="bg-white rounded-2xl p-8 shadow-lg space-y-6">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files that are placed on your device when you visit our Platform. They help us provide you with a better experience by remembering your preferences, understanding how you use our services, and improving our Platform's functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use cookies for various purposes, including:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Enabling essential Platform functionality</li>
              <li>Remembering your preferences and settings</li>
              <li>Understanding how you interact with our Platform</li>
              <li>Improving our services and user experience</li>
              <li>Delivering personalized content and recommendations</li>
              <li>Analyzing traffic and usage patterns</li>
              <li>Preventing fraud and enhancing security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Essential Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies are necessary for the Platform to function properly. They enable basic features like page navigation, secure access to your account, and order processing. The Platform cannot function properly without these cookies.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Performance Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies collect information about how visitors use our Platform, such as which pages are visited most often and if users receive error messages. This helps us improve the Platform's performance and user experience.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Functional Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies allow the Platform to remember choices you make (such as your language preference or location) and provide enhanced, personalized features. They may also be used to remember changes you've made to text size, fonts, and other customizable parts of web pages.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 Targeting/Advertising Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies are used to deliver advertisements more relevant to you and your interests. They may be set by us or by third-party advertising partners. They remember that you have visited our Platform and may share this information with other organizations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.5 Analytics Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              We use analytics services like Google Analytics to understand how users interact with our Platform. These cookies help us analyze traffic, measure the effectiveness of our content, and improve our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may allow third-party service providers to place cookies on your device for purposes such as:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Payment processing</li>
              <li>Analytics and performance monitoring</li>
              <li>Social media integration</li>
              <li>Advertising and marketing</li>
              <li>Customer support services</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              These third parties have their own privacy policies, and we have no control over their cookies. We recommend reviewing their privacy policies to understand their data practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cookie Duration</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Session Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These are temporary cookies that expire when you close your browser. They help us track your activities during a single browsing session.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Persistent Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              These cookies remain on your device for a set period or until you delete them. They help us remember your preferences and settings for future visits.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Managing Your Cookie Preferences</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You have several options to manage cookies:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">6.1 Browser Settings</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Most browsers allow you to control cookies through their settings. You can typically:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>View and delete cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when closing your browser</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              Please note that blocking or deleting cookies may affect your ability to use certain features of our Platform.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">6.2 Opt-Out Tools</h3>
            <p className="text-gray-700 leading-relaxed">
              You can opt out of interest-based advertising by visiting industry opt-out pages or using browser extensions that block tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Do Not Track Signals</h2>
            <p className="text-gray-700 leading-relaxed">
              Some browsers have "Do Not Track" features that signal to websites that you do not want to be tracked. Currently, there is no industry standard for responding to Do Not Track signals, and our Platform does not respond to such signals at this time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Consent</h2>
            <p className="text-gray-700 leading-relaxed">
              By using our Platform, you consent to the use of cookies as described in this Cookie Policy. When you first visit our Platform, you will be shown a cookie banner asking for your consent. You can change your cookie preferences at any time through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in technology, law, or our business practices. We will notify you of significant changes by posting the updated policy on our Platform with a new "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you have questions about our use of cookies or this Cookie Policy, please contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-gray-900 font-semibold mb-2">TrendRush India</p>
              <p className="text-gray-700">Email: support@trendrushindia.com</p>
              <p className="text-gray-700">Phone: +91 8247034033</p>
              <p className="text-gray-700 mt-2">India</p>
            </div>
          </section>

          <section className="bg-coral-light p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Common Browser Cookie Settings</h3>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Microsoft Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
